import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import type {
	CompanySignupDraftContentDTO,
	CompanySignupDraftDTO,
	CompanySignupDraftSavedDTO,
	CompanySignupDTO,
	CompanySignupResultDTO,
	FreemiumCompanyRowDTO,
} from '@libs/dtos/merit/freemium.dto';
import type { Observable } from 'rxjs';

/**
 * Tier gratuito: el alta pública (sin sesión) y la vista de administración.
 *
 * Los dos viven en el mismo servicio porque son las dos caras de lo mismo, pero
 * `signup` es el ÚNICO método que se llama sin estar autenticado — se apunta a
 * `/public/...` y no lleva ni espera ninguna credencial de vuelta.
 */
@Injectable({ providedIn: 'root' })
export class FreemiumService {
	private readonly http = inject(HttpClient);
	private readonly publicUrl = `${environment.API_URL}/public/company`;
	private readonly adminUrl = `${environment.API_URL}/admin/freemium`;

	/** Alta gratuita desde el formulario público. */
	/** Logo o imagen de la propiedad, subido ANTES del alta (08/09): devuelve la ruta que va en el alta. */
	uploadSignupLogo(file: File): Observable<{ logoPath: string }> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<{ logoPath: string }>(`${environment.API_URL}/public/company/signup/logo`, formData);
	}

	signup(payload: CompanySignupDTO): Observable<CompanySignupResultDTO> {
		return this.http.post<CompanySignupResultDTO>(`${this.publicUrl}/signup`, payload);
	}

	/**
	 * Guarda un alta EMPEZADA y no terminada (flujos 7 y 8).
	 *
	 * Exige el consentimiento PDPL, así que el formulario no lo llama hasta que
	 * la casilla está marcada: antes de eso lo tecleado no sale del navegador.
	 */
	saveDraft(payload: CompanySignupDraftDTO): Observable<CompanySignupDraftSavedDTO> {
		return this.http.post<CompanySignupDraftSavedDTO>(`${this.publicUrl}/signup/draft`, payload);
	}

	/** Recupera un alta a medias. El token va en el cuerpo, no en la URL. */
	resumeDraft(token: string): Observable<CompanySignupDraftContentDTO> {
		return this.http.post<CompanySignupDraftContentDTO>(`${this.publicUrl}/signup/draft/resume`, { token });
	}

	/**
	 * Último guardado al cerrar la pestaña.
	 *
	 * `sendBeacon` y no una petición normal porque el navegador cancela lo que
	 * quede en vuelo al descargar la página — justo en el caso que estos flujos
	 * vienen a atender: escribir tres campos e irse. El navegador se encarga de
	 * entregarlo después.
	 *
	 * Devuelve si se ha podido encolar. No hay respuesta que leer: si este es el
	 * primer guardado, el token se queda en el servidor y la persona vuelve por
	 * el enlace del correo, que es exactamente para lo que existe el flujo 8.
	 */
	saveDraftOnUnload(payload: CompanySignupDraftDTO): boolean {
		if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
		const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
		return navigator.sendBeacon(`${this.publicUrl}/signup/draft`, blob);
	}

	/** Empresas en freemium, las que vencen antes primero. */
	companies(): Observable<FreemiumCompanyRowDTO[]> {
		return this.http.get<FreemiumCompanyRowDTO[]>(`${this.adminUrl}/companies`);
	}

	/** Mueve el vencimiento del acceso gratuito de una empresa. */
	updateExpiry(companyId: number, expiresAt: string): Observable<FreemiumCompanyRowDTO> {
		return this.http.patch<FreemiumCompanyRowDTO>(`${this.adminUrl}/companies/${companyId}/expiry`, {
			expiresAt,
		});
	}
}
